import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Logo, { LogoEditButton } from '../shared/Logo';
import Login from '../auth/Login';
import api from '../../utils/api';
import emailjs from '@emailjs/browser';
import './LandingPage.css';

const STORAGE_KEY = 'solart_landing_content';
const STORAGE_VERSION = 'v8'; // bump this whenever defaults change

const menuItems = [
  { label: 'Home', href: '#hero' },
  { label: 'Services', href: '#services' },
  { label: 'Projects', href: '#projects' },
  { label: 'Our Clients', href: '#clients' },
  { label: 'Trends', href: '#trends' },
  { label: 'Contact', href: '#contact' },
];

/* Solar images from India — royalty-free via Unsplash */
const IMAGES = {
  heroMain: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1600&q=80',
  heroOverlay: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1600&q=80',
  residential: '/images/residential-solar.jpg',
  commercial: 'https://images.unsplash.com/photo-1624397640148-949b1732bb0a?w=600&q=80',
  hospital: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=600&q=80',
  panels1: 'https://images.unsplash.com/photo-1613665813446-82a78c468a1d?w=600&q=80',
  panels2: 'https://images.unsplash.com/photo-1532601224476-15c79f2f7a51?w=600&q=80',
  worker: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&q=80',
  ctaBg: 'https://images.unsplash.com/photo-1497440001374-f26997328c1b?w=1600&q=80',
};

const defaultContent = {
  hero: {
    badge: '☀️ India\'s Trusted Solar Partner',
    title: 'Powering India\'s Future with Clean Solar Energy',
    subtitle: 'From rooftop solar installations to large-scale solar farms across Andhra Pradesh, Telangana, and throughout India — delivering end-to-end solar solutions trusted by 1000+ customers.',
  },
  stats: [
    { value: '500+', label: 'Projects Completed' },
    { value: '50MW+', label: 'Capacity Installed' },
    { value: '₹12Cr+', label: 'Customer Savings' },
    { value: '98%', label: 'Satisfaction Rate' },
  ],
  services: [
    { icon: '🏠', title: 'Residential Solar', desc: 'Rooftop installations for homes with net metering and 25-year warranty panels.', image: IMAGES.residential },
    { icon: '🏭', title: 'Commercial Solar', desc: 'Large-scale solar plants for factories, offices and commercial buildings.', image: IMAGES.commercial },
    { icon: '🔧', title: 'Maintenance & AMC', desc: 'Annual maintenance and support services will be provided.', image: IMAGES.worker },
    { icon: '🔋', title: 'Battery Storage', desc: 'Hybrid & off-grid systems with lithium-ion battery backup solutions.', image: IMAGES.panels1 },
    { icon: '📊', title: 'Energy Audit', desc: 'Complete energy consumption analysis and custom solar design.', image: IMAGES.panels2 },
    { icon: '📋', title: 'Subsidy Assistance', desc: 'End-to-end help with government solar subsidies and documentation.', image: IMAGES.hospital },
  ],
  trends: [
    { tag: '2026 Trend', title: 'AI-Powered Solar Monitoring', desc: 'Machine learning algorithms now predict panel degradation, optimize tilt angles in real-time, and forecast energy generation with 98% accuracy.', image: '🤖' },
    { tag: 'Innovation', title: 'Perovskite-Silicon Tandem Cells', desc: 'Next-gen tandem solar cells are crossing 33% efficiency in commercial production — a 40% jump over traditional silicon.', image: '⚡' },
    { tag: 'Sustainability', title: 'Agrivoltaics — Solar + Farming', desc: 'Dual-use land with elevated solar panels allowing crops to grow underneath. Boosting farm income while generating clean energy.', image: '🌾' },
    { tag: 'Storage', title: 'Sodium-Ion Batteries', desc: 'Affordable, fire-safe sodium-ion batteries are replacing lithium for home storage at 60% lower cost.', image: '🔋' },
    { tag: 'Smart Grid', title: 'Vehicle-to-Grid (V2G) Integration', desc: 'EV owners now sell stored solar energy back to the grid during peak hours, turning cars into mobile power banks.', image: '🚗' },
    { tag: 'Design', title: 'Building-Integrated Photovoltaics', desc: 'Solar roof tiles, facades, and windows are replacing traditional panels — turning entire buildings into power plants.', image: '🏗️' },
  ],
  benefits: [
    { title: 'Slash Electricity Bills', desc: 'Reduce your monthly electricity bill by up to 90%. With net metering, sell excess power back to the grid.' },
    { title: 'Government Subsidies', desc: 'Get up to 40% subsidy under PM Surya Ghar scheme for residential installations up to 3kW.' },
    { title: '25-Year Returns', desc: 'Solar panels last 25+ years with minimal maintenance. ROI typically achieved within 3-5 years.' },
    { title: 'Increase Property Value', desc: 'Homes with solar sell for 4-6% more. It\'s an investment that appreciates while saving you money.' },
    { title: 'Energy Independence', desc: 'With battery storage, say goodbye to power cuts. Generate, store, and use your own clean energy.' },
    { title: 'Reduce Carbon Footprint', desc: 'A 5kW system prevents ~7 tonnes of CO₂ per year — equivalent to planting 350 trees annually.' },
  ],
  showcases: [
    { tag: 'Agriculture', title: 'Solar Water Pump System', desc: 'Off-grid solar powered water pumping system providing reliable irrigation for agriculture.', stat: 'Sustainable Farming', image: '/images/project_one.jpeg' },
    { tag: 'Commercial', title: 'Large Scale Rooftop Solar', desc: 'Extensive rooftop solar array for a commercial facility to offset high energy consumption.', stat: 'High Energy Yield', image: '/images/project_two.jpeg' },
    { tag: 'Residential', title: 'Rooftop Solar Installation', desc: 'Professional installation of rooftop solar panels by our expert team.', stat: 'Quick Setup', image: '/images/project_three.jpeg' },
    { tag: 'Innovative', title: 'Solar Tree Structure', desc: 'Space-saving solar tree installation designed for high efficiency with minimal land footprint.', stat: 'Architectural & Efficient', image: '/images/project_four.jpeg' }
  ],
  testimonials: [
    { name: 'Rajesh Kumar', role: 'Homeowner, Hyderabad', text: 'Our electricity bill dropped from ₹8,000 to ₹200/month. The team handled everything from permits to installation seamlessly.' },
    { name: 'Priya Sharma', role: 'Factory Owner, Vizag', text: 'We installed 100kW on our factory roof. ROI came in 3.5 years instead of the projected 5. Excellent service and monitoring.' },
    { name: 'Dr. Anand Rao', role: 'Hospital Director', text: 'The battery backup solution keeps our critical systems running 24/7. Maintenance team from DWCRA Group responds within hours.' },
  ],
  contact: {
    title: 'Ready to Go Solar?',
    subtitle: 'Get a free site survey and custom solar proposal. Our team will design the perfect system for your needs.',
    phone: '+91 9845878643',
    email: 'cherieshpower.ap@gmail.com',
    address: 'Shop No.1, Goli Samba Siva Residency, APIIC Auto Nagar, Mangalagiri',
    city: 'Guntur - 522503, Andhra Pradesh',
  },
};

function loadContent() {
  try {
    const version = localStorage.getItem(STORAGE_KEY + '_version');
    if (version !== STORAGE_VERSION) {
      // Defaults have changed — clear old cache
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STORAGE_KEY + '_version', STORAGE_VERSION);
      return defaultContent;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return defaultContent;
}

function isValidLandingContent(content) {
  if (!content || typeof content !== 'object' || Array.isArray(content)) return false;

  const requiredKeys = [
    'hero',
    'stats',
    'services',
    'trends',
    'benefits',
    'showcases',
    'testimonials',
    'contact',
  ];

  return requiredKeys.every((key) => key in content);
}

// ── Edit button shown only to logged-in users ──
function EditBtn({ onClick, label }) {
  return (
    <button className="landing-edit-btn" onClick={onClick} title={`Edit ${label}`}>
      ✏️ Edit {label}
    </button>
  );
}

// ── Generic edit modal ──
function EditModal({ title, children, onSave, onCancel }) {
  return (
    <div className="landing-edit-overlay" onClick={onCancel}>
      <div className="landing-edit-modal" onClick={(e) => e.stopPropagation()}>
        <div className="landing-edit-modal-header">
          <h3>Edit {title}</h3>
          <button className="landing-edit-close" onClick={onCancel}>✕</button>
        </div>
        <div className="landing-edit-modal-body">{children}</div>
        <div className="landing-edit-modal-footer">
          <button className="landing-edit-cancel" onClick={onCancel}>Cancel</button>
          <button className="landing-edit-save" onClick={onSave}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

function readImageFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve('');
      return;
    }
    if (!file.type.startsWith('image/')) {
      reject(new Error('Please select a valid image file.'));
      return;
    }

    // Compress large images to reduce localStorage usage.
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const maxDimension = 1280;
      const ratio = Math.min(1, maxDimension / Math.max(img.width, img.height));
      const targetWidth = Math.max(1, Math.round(img.width * ratio));
      const targetHeight = Math.max(1, Math.round(img.height * ratio));

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Failed to process image.'));
        return;
      }

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      const isPng = file.type === 'image/png';
      const mimeType = isPng ? 'image/png' : 'image/jpeg';
      const quality = isPng ? undefined : 0.82;
      const dataUrl = canvas.toDataURL(mimeType, quality);

      URL.revokeObjectURL(objectUrl);
      resolve(dataUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to read image file.'));
    };
    img.src = objectUrl;
  });
}

// ── List editor (for services, trends, benefits, showcases, testimonials, stats) ──
function ListEditor({ items, fields, onChange }) {
  const update = (idx, field, value) => {
    const copy = items.map((item, i) => i === idx ? { ...item, [field]: value } : item);
    onChange(copy);
  };
  const addItem = () => {
    const empty = {};
    fields.forEach((f) => (empty[f.key] = ''));
    onChange([...items, empty]);
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));

  const handleImageFileChange = async (idx, fieldKey, file) => {
    try {
      const imageDataUrl = await readImageFileAsDataUrl(file);
      update(idx, fieldKey, imageDataUrl || '');
    } catch (err) {
      window.alert(err.message || 'Unable to upload image.');
    }
  };

  return (
    <div className="landing-list-editor">
      {items.map((item, idx) => (
        <div key={idx} className="landing-list-editor-item">
          <div className="landing-list-editor-item-header">
            <span className="landing-list-editor-number">#{idx + 1}</span>
            <button className="landing-list-editor-remove" onClick={() => removeItem(idx)}>🗑️ Remove</button>
          </div>
          {fields.map((f) => (
            <label key={f.key} className="landing-edit-label">
              {f.label}
              {f.type === 'textarea' ? (
                <textarea value={item[f.key] || ''} onChange={(e) => update(idx, f.key, e.target.value)} rows={3} />
              ) : f.type === 'image' ? (
                <>
                  <input type="text" value={item[f.key] || ''} placeholder="Paste image URL (optional)"
                    onChange={(e) => update(idx, f.key, e.target.value)} />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageFileChange(idx, f.key, e.target.files?.[0])}
                  />
                  {item[f.key] && (
                    <div style={{ marginTop: 8 }}>
                      <img
                        src={item[f.key]}
                        alt="Preview"
                        style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                      />
                    </div>
                  )}
                </>
              ) : (
                <input type="text" value={item[f.key] || ''} onChange={(e) => update(idx, f.key, e.target.value)} />
              )}
            </label>
          ))}
        </div>
      ))}
      <button className="landing-list-editor-add" onClick={addItem}>+ Add Item</button>
    </div>
  );
}

export default function LandingPage({ initialShowLogin = false }) {
  const { user, logout } = useAuth();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [content, setContent] = useState(loadContent);
  const [editing, setEditing] = useState(null); // which section is being edited
  const [draft, setDraft] = useState(null);     // draft copy while editing
  const [showLoginDrawer, setShowLoginDrawer] = useState(initialShowLogin);
  const [showWhatsAppForm, setShowWhatsAppForm] = useState(false);
  const [whatsAppFormType, setWhatsAppFormType] = useState('quote');
  const [whatsAppData, setWhatsAppData] = useState({ name: '', phone: '', details: '' });
  const navMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  const isLoggedIn = !!user;
  const isAdmin = user?.role === 'admin';
  const isDev = user?.email === 'dev@solar.com';
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchSharedContent = async () => {
      try {
        const response = await api.get('/landing-content');
        const serverContent = response?.data?.content;

        if (!serverContent || !isMounted || !isValidLandingContent(serverContent)) return;

        setContent(serverContent);
        localStorage.setItem(STORAGE_KEY + '_version', STORAGE_VERSION);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(serverContent));
      } catch {
        // Keep local cache/default content when server content is unavailable.
      }
    };

    fetchSharedContent();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!mobileMenu) return undefined;

    const handleOutsidePointer = (event) => {
      const target = event.target;
      const menuEl = navMenuRef.current;
      const hamburgerEl = hamburgerRef.current;

      if (menuEl?.contains(target) || hamburgerEl?.contains(target)) {
        return;
      }

      setMobileMenu(false);
    };

    document.addEventListener('pointerdown', handleOutsidePointer);
    return () => document.removeEventListener('pointerdown', handleOutsidePointer);
  }, [mobileMenu]);

  const scrollTo = (href) => {
    setMobileMenu(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const saveContent = useCallback(async (updated) => {
    try {
      await api.put('/landing-content', { content: updated });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      localStorage.setItem(STORAGE_KEY + '_version', STORAGE_VERSION);
      setContent(updated);
      return true;
    } catch (err) {
      const maybeStorageError = err && (err.name === 'QuotaExceededError' || err.code === 22);
      if (maybeStorageError) {
        window.alert('Unable to save changes. Uploaded images are too large for browser storage. Please use smaller images or image URLs.');
      } else {
        window.alert('Unable to sync landing content to server. Check backend connection and admin login.');
      }
      return false;
    }
  }, []);

  const openEdit = (section) => {
    setEditing(section);
    setDraft(JSON.parse(JSON.stringify(content)));
  };

  const cancelEdit = () => { setEditing(null); setDraft(null); };

  const saveEdit = async () => {
    const saved = await saveContent(draft);
    if (!saved) return;
    setEditing(null);
    setDraft(null);
  };

  const resetToDefaults = () => {
    saveContent(defaultContent);
  };

  const openWhatsAppForm = (type = 'quote') => {
    setWhatsAppFormType(type);
    setWhatsAppData({ name: '', phone: '', details: '' });
    setShowWhatsAppForm(true);
  };

  const sendToWhatsApp = async () => {
    const WHATSAPP_NUMBER = '919845878643';
    const label = whatsAppFormType === 'project' ? 'Start Project' : whatsAppFormType === 'maintenance' ? 'Maintenance Request' : 'Free Quote';
    const msg = `Hello Cheriesh Power Technologies! I would like to request a ${label}.\n\nName: ${whatsAppData.name}\nPhone: ${whatsAppData.phone}\nDetails: ${whatsAppData.details}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
    setShowWhatsAppForm(false);

    // Send email notification to admin via EmailJS
    try {
      await emailjs.send(
        'service_hz0r1mf',    // EmailJS Service ID
        'template_yaomb1i',   // EmailJS Template ID
        {
          name: whatsAppData.name,
          phone: whatsAppData.phone,
          location: 'N/A',
          type: label,
          size: 'N/A',
          details: whatsAppData.details,
        },
        'pgIHN6afJu3UOvUOs'     // EmailJS Public Key
      );
      console.log('Email sent successfully via EmailJS');
    } catch (err) {
      console.error('Email notification failed:', err.text || err.message);
    }
  };

  return (
    <div className="landing">
      {/* ===== Navbar ===== */}
      <nav className={`landing-nav ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-container">
          <a href="#hero" className="nav-logo" onClick={() => scrollTo('#hero')}>
            <Logo size={100} />
            <span className="nav-brand-text">
              <span className="nav-brand-name">Cheriesh</span>
              <span className="nav-brand-sub">Power Technologies</span>
            </span>
          </a>
          {isDev && <LogoEditButton />}

          {mobileMenu && (
            <div className="nav-mobile-overlay" onClick={() => setMobileMenu(false)} />
          )}

          <div ref={navMenuRef} className={`nav-menu ${mobileMenu ? 'open' : ''}`}>
            {menuItems.map((item) => (
              <a key={item.href} href={item.href} className="nav-menu-link"
                onClick={(e) => { e.preventDefault(); scrollTo(item.href); setMobileMenu(false); }}>
                {item.label}
              </a>
            ))}
            {user ? (
              <Link to="/dashboard" className="nav-cta-btn" onClick={() => setMobileMenu(false)}>Dashboard</Link>
            ) : null}
            {user && (
              <button className="nav-logout-btn" onClick={() => { setMobileMenu(false); handleLogout(); }}>
                🚪 Logout
              </button>
            )}
          </div>

          <button ref={hamburgerRef} className="nav-hamburger" onClick={() => setMobileMenu(!mobileMenu)}
            aria-label="Toggle menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ===== Admin edit toolbar (admin only) ===== */}
      {isAdmin && (
        <div className="landing-edit-toolbar">
          <span>✏️ Edit Mode — Click any section's edit button to modify content</span>
          <button className="landing-reset-btn" onClick={resetToDefaults}>↩️ Reset All to Defaults</button>
        </div>
      )}

      {/* ===== Hero ===== */}
      <section id="hero" className="hero-section">
        <div className="hero-bg-image" style={{ backgroundImage: `url(${IMAGES.heroMain})` }} />
        <div className="hero-bg-overlay" />
        <div className="hero-content">
          {isAdmin && <EditBtn onClick={() => openEdit('hero')} label="Hero" />}
          <span className="hero-badge">{content.hero.badge}</span>
          <h1>{content.hero.title}</h1>
          <p>{content.hero.subtitle}</p>
          <div className="hero-buttons">
            <button onClick={() => openWhatsAppForm('quote')} className="btn-landing btn-landing-primary">
              Get Free Quote →
            </button>
            <a href="#services" className="btn-landing btn-landing-outline"
              onClick={(e) => { e.preventDefault(); scrollTo('#services'); }}>
              Our Services
            </a>
          </div>
          <div className="hero-stats">
            {content.stats.map((s, i) => (
              <div key={i} className="hero-stat">
                <span className="hero-stat-value">{s.value}</span>
                <span className="hero-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Services ===== */}
      <section id="services" className="section">
        <div className="section-container">
          {isAdmin && <EditBtn onClick={() => openEdit('services')} label="Services" />}
          <div className="section-header-center">
            <span className="section-tag">What We Offer</span>
            <h2 className="section-title">Complete Solar Solutions</h2>
            <p className="section-subtitle-center">From rooftop installations to large-scale solar farms — we handle everything.</p>
          </div>
          <div className="services-grid">
            {content.services.map((s, i) => (
              <div key={i} className="service-card">
                {s.image && <div className="service-card-img" style={{ backgroundImage: `url(${s.image})` }} />}
                <div className="service-card-body">
                  <div className="service-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Why Solar ===== */}
      <section id="why-solar" className="section">
        <div className="section-container">
          {isAdmin && <EditBtn onClick={() => openEdit('benefits')} label="Benefits" />}
          <div className="section-header-center">
            <span className="section-tag">Why Go Solar?</span>
            <h2 className="section-title">Benefits That Pay for Themselves</h2>
          </div>
          <div className="benefits-grid">
            {content.benefits.map((b, i) => (
              <div key={i} className="benefit-card">
                <div className="benefit-number">{String(i + 1).padStart(2, '0')}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Projects Showcase ===== */}
      <section id="projects" className="section section-alt">
        <div className="section-container">
          {isAdmin && <EditBtn onClick={() => openEdit('showcases')} label="Projects" />}
          <div className="section-header-center">
            <span className="section-tag">Our Work</span>
            <h2 className="section-title">Recent Installations</h2>
          </div>
          <div className="projects-showcase">
            {content.showcases.map((s, i) => (
              <div key={i} className="showcase-card">
                <div className="showcase-img" style={s.image ? { backgroundImage: `url(${s.image})` } : undefined}>
                  {!s.image && s.icon}
                </div>
                <div className="showcase-info">
                  <span className="showcase-tag">{s.tag}</span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <span className="showcase-stat">📈 {s.stat}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Our Clients ===== */}
      <section id="clients" className="clients-section">
        <div className="section-container">
          <div className="section-header-center">
            <span className="section-tag">Trusted By</span>
            <h2 className="section-title">Our Clients</h2>
          </div>
        </div>
        <div className="clients-track-wrapper">
          <div className="clients-track">
            {[
              { name: 'Indian Railways', abbr: 'IR', color: '#c0392b', logo: '/clientlogos/indianrailway.png' },
              { name: 'NREDCAP', abbr: 'NR', color: '#27ae60', logo: '/clientlogos/ncap.png' },
              { name: 'TSREDCO', abbr: 'TS', color: '#2980b9', logo: '/clientlogos/tsredco.png' },
              { name: 'SRM University AP', abbr: 'SRM', color: '#6d4c41', logo: '/clientlogos/srm.jpeg' },
              { name: 'Sangam', abbr: 'SG', color: '#e67e22', logo: '/clientlogos/sangam.jpeg' },
              { name: 'AP Police Housing', abbr: 'APH', color: '#2c3e50', logo: '/clientlogos/appoliceehouse.png' },
              { name: 'NCC', abbr: 'NCC', color: '#8e44ad', logo: '/clientlogos/ncc.png' },
              { name: 'Amrita Vishwa Vidyapeetham', abbr: 'AVV', color: '#c0392b', logo: '/clientlogos/amrita.png' },
              { name: 'Healing Pharma', abbr: 'HP', color: '#16a085', logo: '/clientlogos/healingpharma.png' },
              { name: 'AP Prisons', abbr: 'APP', color: '#2c3e50', logo: '/clientlogos/apprisons.png' },
              { name: 'AG Industries', abbr: 'AG', color: '#2980b9', logo: '/clientlogos/ag.png' },
              { name: 'KMV Group', abbr: 'KMV', color: '#e74c3c', logo: '/clientlogos/kmv.png' },
              // duplicate for seamless loop
              { name: 'Indian Railways', abbr: 'IR', color: '#c0392b', logo: '/clientlogos/indianrailway.png' },
              { name: 'NREDCAP', abbr: 'NR', color: '#27ae60', logo: '/clientlogos/ncap.png' },
              { name: 'TSREDCO', abbr: 'TS', color: '#2980b9', logo: '/clientlogos/tsredco.png' },
              { name: 'SRM University AP', abbr: 'SRM', color: '#6d4c41', logo: '/clientlogos/srm.jpeg' },
              { name: 'Sangam', abbr: 'SG', color: '#e67e22', logo: '/clientlogos/sangam.jpeg' },
              { name: 'AP Police Housing', abbr: 'APH', color: '#2c3e50', logo: '/clientlogos/appoliceehouse.png' },
              { name: 'NCC', abbr: 'NCC', color: '#8e44ad', logo: '/clientlogos/ncc.png' },
              { name: 'Amrita Vishwa Vidyapeetham', abbr: 'AVV', color: '#c0392b', logo: '/clientlogos/amrita.png' },
              { name: 'Healing Pharma', abbr: 'HP', color: '#16a085', logo: '/clientlogos/healingpharma.png' },
              { name: 'AP Prisons', abbr: 'APP', color: '#2c3e50', logo: '/clientlogos/apprisons.png' },
              { name: 'AG Industries', abbr: 'AG', color: '#2980b9', logo: '/clientlogos/ag.png' },
              { name: 'KMV Group', abbr: 'KMV', color: '#e74c3c', logo: '/clientlogos/kmv.png' },
            ].map((c, i) => (
              <div key={i} className="client-card">
                <div className="client-logo-wrap">
                  <img
                    src={c.logo}
                    alt={c.name}
                    className="client-logo-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="client-logo-circle" style={{ background: c.color, display: 'none' }}>{c.abbr}</div>
                </div>
                <span className="client-name">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Trends ===== */}
      <section id="trends" className="section section-dark">
        <div className="section-container">
          {isAdmin && <EditBtn onClick={() => openEdit('trends')} label="Trends" />}
          <div className="section-header-center">
            <span className="section-tag tag-light">🔥 What's New in 2026</span>
            <h2 className="section-title" style={{ color: 'white' }}>Solar Industry Trends</h2>
            <p className="section-subtitle-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
              The solar industry is evolving fast. Here are the breakthroughs shaping the future.
            </p>
          </div>
          <div className="trends-grid">
            {content.trends.map((t, i) => (
              <div key={i} className="trend-card">
                <div className="trend-icon">{t.image}</div>
                <span className="trend-tag">{t.tag}</span>
                <h3>{t.title}</h3>
                <p>{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Contact / CTA ===== */}
      <section id="contact" className="section section-cta">
        <div className="cta-bg-image" style={{ backgroundImage: `url(${IMAGES.ctaBg})` }} />
        <div className="cta-bg-overlay" />
        <div className="section-container" style={{ textAlign: 'center', position: 'relative', zIndex: 2 }}>
          {isAdmin && <EditBtn onClick={() => openEdit('contact')} label="Contact" />}
          <h2 style={{ fontSize: 36, color: 'white', marginBottom: 16 }}>{content.contact.title}</h2>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 18, maxWidth: 600, margin: '0 auto 32px' }}>
            {content.contact.subtitle}
          </p>
          <div className="cta-buttons">
            <button onClick={() => openWhatsAppForm('project')} className="btn-landing btn-landing-white">Start Your Project →</button>
            <button onClick={() => openWhatsAppForm('maintenance')} className="btn-landing btn-landing-outline-white">Request Maintenance</button>
          </div>
          <div className="cta-contact-info">
            <span>📞 {content.contact.phone}</span>
            <span>📧 {content.contact.email}</span>
            <span>📍 {content.contact.city}</span>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="landing-footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-brand">
              <h3><Logo size={24} /> Cheriesh Power Technologies</h3>
              <p>End-to-end solar solutions for homes, businesses, and institutions.</p>
            </div>
            <div>
              <h4>Quick Links</h4>
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollTo('#services'); }}>Services</a>
              <a href="#projects" onClick={(e) => { e.preventDefault(); scrollTo('#projects'); }}>Projects</a>
              <a href="#clients" onClick={(e) => { e.preventDefault(); scrollTo('#clients'); }}>Our Clients</a>
              <a href="#trends" onClick={(e) => { e.preventDefault(); scrollTo('#trends'); }}>Trends</a>
            </div>

            <div>
              <h4>Contact</h4>
              <span>📞 {content.contact.phone}</span>
              <span>📧 {content.contact.email}</span>
              <span>📍 {content.contact.address}</span>
              <span>{content.contact.city}</span>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2026 Cheriesh Power Technologies. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ===== EDIT MODALS ===== */}

      {editing === 'hero' && draft && (
        <EditModal title="Hero Section" onSave={saveEdit} onCancel={cancelEdit}>
          <label className="landing-edit-label">
            Badge Text
            <input type="text" value={draft.hero.badge}
              onChange={(e) => setDraft({ ...draft, hero: { ...draft.hero, badge: e.target.value } })} />
          </label>
          <label className="landing-edit-label">
            Main Title
            <input type="text" value={draft.hero.title}
              onChange={(e) => setDraft({ ...draft, hero: { ...draft.hero, title: e.target.value } })} />
          </label>
          <label className="landing-edit-label">
            Subtitle
            <textarea rows={3} value={draft.hero.subtitle}
              onChange={(e) => setDraft({ ...draft, hero: { ...draft.hero, subtitle: e.target.value } })} />
          </label>
          <h4 style={{ marginTop: 16 }}>Stats</h4>
          <ListEditor
            items={draft.stats}
            fields={[
              { key: 'value', label: 'Value', type: 'text' },
              { key: 'label', label: 'Label', type: 'text' },
            ]}
            onChange={(stats) => setDraft({ ...draft, stats })}
          />
        </EditModal>
      )}

      {editing === 'services' && draft && (
        <EditModal title="Services" onSave={saveEdit} onCancel={cancelEdit}>
          <ListEditor
            items={draft.services}
            fields={[
              { key: 'icon', label: 'Icon (emoji)', type: 'text' },
              { key: 'title', label: 'Title', type: 'text' },
              { key: 'desc', label: 'Description', type: 'textarea' },
            ]}
            onChange={(services) => setDraft({ ...draft, services })}
          />
        </EditModal>
      )}

      {editing === 'trends' && draft && (
        <EditModal title="Trends" onSave={saveEdit} onCancel={cancelEdit}>
          <ListEditor
            items={draft.trends}
            fields={[
              { key: 'image', label: 'Icon (emoji)', type: 'text' },
              { key: 'tag', label: 'Tag Label', type: 'text' },
              { key: 'title', label: 'Title', type: 'text' },
              { key: 'desc', label: 'Description', type: 'textarea' },
            ]}
            onChange={(trends) => setDraft({ ...draft, trends })}
          />
        </EditModal>
      )}

      {editing === 'benefits' && draft && (
        <EditModal title="Benefits" onSave={saveEdit} onCancel={cancelEdit}>
          <ListEditor
            items={draft.benefits}
            fields={[
              { key: 'title', label: 'Title', type: 'text' },
              { key: 'desc', label: 'Description', type: 'textarea' },
            ]}
            onChange={(benefits) => setDraft({ ...draft, benefits })}
          />
        </EditModal>
      )}

      {editing === 'showcases' && draft && (
        <EditModal title="Project Showcases" onSave={saveEdit} onCancel={cancelEdit}>
          <ListEditor
            items={draft.showcases}
            fields={[
              { key: 'image', label: 'Project Image', type: 'image' },
              { key: 'icon', label: 'Icon (emoji, optional when no image)', type: 'text' },
              { key: 'tag', label: 'Tag (e.g. Residential)', type: 'text' },
              { key: 'title', label: 'Title', type: 'text' },
              { key: 'desc', label: 'Description', type: 'textarea' },
              { key: 'stat', label: 'Key Stat', type: 'text' },
            ]}
            onChange={(showcases) => setDraft({ ...draft, showcases })}
          />
        </EditModal>
      )}

      {editing === 'testimonials' && draft && (
        <EditModal title="Testimonials" onSave={saveEdit} onCancel={cancelEdit}>
          <ListEditor
            items={draft.testimonials}
            fields={[
              { key: 'name', label: 'Name', type: 'text' },
              { key: 'role', label: 'Role / Location', type: 'text' },
              { key: 'text', label: 'Testimonial', type: 'textarea' },
            ]}
            onChange={(testimonials) => setDraft({ ...draft, testimonials })}
          />
        </EditModal>
      )}


      {editing === 'contact' && draft && (
        <EditModal title="Contact Section" onSave={saveEdit} onCancel={cancelEdit}>
          <label className="landing-edit-label">
            Heading
            <input type="text" value={draft.contact.title}
              onChange={(e) => setDraft({ ...draft, contact: { ...draft.contact, title: e.target.value } })} />
          </label>
          <label className="landing-edit-label">
            Subtitle
            <textarea rows={2} value={draft.contact.subtitle}
              onChange={(e) => setDraft({ ...draft, contact: { ...draft.contact, subtitle: e.target.value } })} />
          </label>
          <label className="landing-edit-label">
            Phone
            <input type="text" value={draft.contact.phone}
              onChange={(e) => setDraft({ ...draft, contact: { ...draft.contact, phone: e.target.value } })} />
          </label>
          <label className="landing-edit-label">
            Email
            <input type="text" value={draft.contact.email}
              onChange={(e) => setDraft({ ...draft, contact: { ...draft.contact, email: e.target.value } })} />
          </label>
          <label className="landing-edit-label">
            Address Line
            <input type="text" value={draft.contact.address}
              onChange={(e) => setDraft({ ...draft, contact: { ...draft.contact, address: e.target.value } })} />
          </label>
          <label className="landing-edit-label">
            City
            <input type="text" value={draft.contact.city}
              onChange={(e) => setDraft({ ...draft, contact: { ...draft.contact, city: e.target.value } })} />
          </label>
        </EditModal>
      )}

      {/* ===== LOGIN DRAWER ===== */}
      {showLoginDrawer && <Login onClose={() => setShowLoginDrawer(false)} />}

      {/* ===== WHATSAPP CONTACT FORM ===== */}
      {showWhatsAppForm && (
        <div className="landing-edit-overlay" onClick={() => setShowWhatsAppForm(false)}>
          <div className="landing-edit-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="landing-edit-modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>💬</span>
                {whatsAppFormType === 'project' ? 'Start Your Project' : whatsAppFormType === 'maintenance' ? 'Request Maintenance' : 'Get Free Quote'}
              </h3>
              <button className="landing-edit-close" onClick={() => setShowWhatsAppForm(false)}>✕</button>
            </div>
            <div className="landing-edit-modal-body">
              <p style={{ marginBottom: 16, color: '#555', fontSize: 14 }}>Fill in your details and we'll connect you on WhatsApp instantly.</p>
              <label className="landing-edit-label">
                Your Name *
                <input
                  type="text"
                  placeholder="e.g. Rajesh Kumar"
                  value={whatsAppData.name}
                  onChange={(e) => setWhatsAppData({ ...whatsAppData, name: e.target.value })}
                />
              </label>
              <label className="landing-edit-label">
                Phone Number *
                <input
                  type="tel"
                  placeholder="e.g. +91 9845878643"
                  value={whatsAppData.phone}
                  onChange={(e) => setWhatsAppData({ ...whatsAppData, phone: e.target.value })}
                />
              </label>
              <label className="landing-edit-label">
                Details / Requirements
                <textarea
                  rows={4}
                  placeholder="Tell us about your property, approximate area, current electricity bill, etc."
                  value={whatsAppData.details}
                  onChange={(e) => setWhatsAppData({ ...whatsAppData, details: e.target.value })}
                />
              </label>
            </div>
            <div className="landing-edit-modal-footer">
              <button className="landing-edit-cancel" onClick={() => setShowWhatsAppForm(false)}>Cancel</button>
              <button
                className="landing-edit-save"
                style={{ backgroundColor: '#25D366', borderColor: '#25D366', display: 'flex', alignItems: 'center', gap: 6 }}
                onClick={sendToWhatsApp}
                disabled={!whatsAppData.name.trim() || !whatsAppData.phone.trim()}
              >
                <span>📲</span> Send on WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
